/**
 * OperationsScreen — route `/rounds/:roundId/operations` (operator+).
 *
 * Operator ingest, two paths sharing the same frozen contract (sign → direct
 * PUT): the bulk folder drop (BulkFolderUploader — one folder, each subfolder a
 * CURP-named expediente) and the per-expediente cards below it. When at least
 * one expediente has been staged, "Procesar ronda" calls `processRound` (202).
 * A 409 means a run is already in flight for this round.
 *
 * Generated operations used:
 *   - signUploads   POST /uploads/sign            (in ExpedienteUploader)
 *   - processRound  POST /rounds/{round_id}/process
 */

import { useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutlineOutlined";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";

import { api, isApiError } from "../api/useApi";
import { IS_DEMO, type ProcessResponse } from "../api/client";
import { DemoBadge } from "../components/DemoBadge";
import { EmptyState } from "../components/EmptyState";
import { ProblemAlert } from "../components/ProblemAlert";
import { RoundTabs } from "../components/RoundTabs";
import { BulkFolderUploader } from "./operations/BulkFolderUploader";
import { ExpedienteUploader } from "./operations/ExpedienteUploader";
import { SingleApplicantForm } from "./operations/SingleApplicantForm";
import { demoProcess } from "./operations/demo";

let cardSeq = 0;
const nextCardId = () => `card-${++cardSeq}`;

export default function OperationsScreen() {
  const { roundId } = useParams<{ roundId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [cards, setCards] = useState<string[]>(() => [nextCardId()]);
  const [uploaded, setUploaded] = useState<Set<string>>(new Set());

  const [singleOpen, setSingleOpen] = useState(false);
  const [createdSingles, setCreatedSingles] = useState<string[]>([]);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processError, setProcessError] = useState<Error | null>(null);
  const [conflict, setConflict] = useState(false);
  const [triggered, setTriggered] = useState<ProcessResponse | null>(null);

  if (!roundId) {
    return (
      <Alert severity="error">
        <AlertTitle>Ronda no especificada</AlertTitle>
        Falta el identificador de la ronda en la URL.
      </Alert>
    );
  }

  function addCard() {
    setCards((prev) => [...prev, nextCardId()]);
  }

  function removeCard(id: string) {
    setCards((prev) => prev.filter((c) => c !== id));
  }

  function markUploaded(expedienteId: string) {
    setUploaded((prev) => new Set(prev).add(expedienteId));
  }

  function markBulkUploaded(expedienteIds: string[]) {
    setUploaded((prev) => {
      const next = new Set(prev);
      for (const id of expedienteIds) next.add(id);
      return next;
    });
  }

  function markSingleCreated(expedienteId: string) {
    setCreatedSingles((prev) => [...prev, expedienteId]);
    setUploaded((prev) => new Set(prev).add(expedienteId));
  }

  async function handleProcess() {
    setConfirmOpen(false);
    setProcessing(true);
    setProcessError(null);
    setConflict(false);
    setTriggered(null);
    try {
      if (IS_DEMO) {
        setTriggered(demoProcess(roundId!));
        navigate(`/rounds/${roundId}/monitor`);
        return;
      }
      const res = await api.POST("/rounds/{round_id}/process", {
        params: { path: { round_id: roundId! } },
      });
      setTriggered(res.data!);
      // The batch runs out-of-band — take the operator straight to the live
      // progress instead of leaving them on the (now static) ingest screen.
      navigate(`/rounds/${roundId}/monitor`);
    } catch (err: unknown) {
      // 409 → a run is already in flight (Conflict carries a generic problem
      // body, so switch on status, not a dedicated code).
      if (isApiError(err) && err.status === 409) {
        setConflict(true);
      } else if (isApiError(err)) {
        setProcessError(err);
      } else {
        setProcessError(err instanceof Error ? err : new Error(String(err)));
      }
    } finally {
      setProcessing(false);
    }
  }

  const uploadedCount = uploaded.size;
  const canProcess = uploadedCount > 0 && !processing;

  return (
    <Stack spacing={2}>
      <RoundTabs roundId={roundId!} currentPath={location.pathname} />
      <Box>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <Typography variant="h1">Operaciones · ingesta</Typography>
          <DemoBadge />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Carga firmada de los 3 documentos por expediente y arranque del
          procesamiento.
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Ronda: {roundId}
        </Typography>
      </Box>

      <BulkFolderUploader
        roundId={roundId}
        onBulkUploaded={(_rid, ids) => markBulkUploaded(ids)}
        onRequestProcess={() => setConfirmOpen(true)}
      />

      <Divider>
        <Typography variant="caption" color="text.secondary">
          o carga expedientes individuales
        </Typography>
      </Divider>

      <Box>
        <Button
          variant="outlined"
          startIcon={<PersonAddAlt1Icon />}
          onClick={() => setSingleOpen(true)}
          disabled={processing}
        >
          Agregar aspirante individual
        </Button>
      </Box>

      {createdSingles.length > 0 && (
        <Alert severity="success" onClose={() => setCreatedSingles([])}>
          <AlertTitle>
            {createdSingles.length === 1
              ? "Aspirante agregado y encolado"
              : `${createdSingles.length} aspirantes agregados y encolados`}
          </AlertTitle>
          {createdSingles.join(", ")} se está(n) procesando con el mismo
          pipeline. El conteo en vivo de la ronda ya los incluye; si la ronda ya
          tenía un reporte cerrado, el reporte se actualiza al reprocesarla.
          <Box sx={{ mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => navigate(`/rounds/${roundId}/monitor`)}
            >
              Ver progreso en el monitor
            </Button>
          </Box>
        </Alert>
      )}

      <SingleApplicantForm
        open={singleOpen}
        roundId={roundId}
        onClose={() => setSingleOpen(false)}
        onCreated={markSingleCreated}
      />

      {triggered && (
        <Alert severity="success">
          <AlertTitle>Ronda en proceso</AlertTitle>
          La ronda <strong>{triggered.round_id}</strong> se encoló (estado:{" "}
          {triggered.status}). El procesamiento corre en segundo plano.
          <Box sx={{ mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => navigate(`/rounds/${roundId}/monitor`)}
            >
              Ver progreso en el monitor
            </Button>
          </Box>
        </Alert>
      )}

      {conflict && (
        <Alert severity="warning" onClose={() => setConflict(false)}>
          <AlertTitle>Ya hay un procesamiento en curso</AlertTitle>
          Esta ronda ya tiene una corrida en marcha. Revisa el avance en el
          monitor antes de volver a procesar.
          <Box sx={{ mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => navigate(`/rounds/${roundId}/monitor`)}
            >
              Ir al monitor
            </Button>
          </Box>
        </Alert>
      )}

      {processError && (
        <ProblemAlert
          error={processError}
          onRetry={() => setConfirmOpen(true)}
        />
      )}

      <Stack spacing={2}>
        {cards.length === 0 ? (
          <EmptyState
            icon={<InsertDriveFileOutlinedIcon fontSize="inherit" />}
            title="Sin expedientes en cola"
            description="Agrega un expediente para cargar sus documentos (INE, certificado y foto)."
            action={
              <Button
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                onClick={addCard}
              >
                Agregar expediente
              </Button>
            }
          />
        ) : (
          cards.map((id) => (
            <ExpedienteUploader
              key={id}
              roundId={roundId}
              onRemove={() => removeCard(id)}
              onUploaded={markUploaded}
            />
          ))
        )}
      </Stack>

      {cards.length > 0 && (
        <Box>
          <Button
            variant="outlined"
            startIcon={<AddCircleOutlineIcon />}
            onClick={addCard}
            disabled={processing}
          >
            Agregar otro expediente
          </Button>
        </Box>
      )}

      <Divider />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{ alignItems: { sm: "center" }, justifyContent: "space-between" }}
      >
        <Typography variant="body2" color="text.secondary">
          {uploadedCount > 0
            ? `${uploadedCount} expediente(s) cargado(s) en esta sesión.`
            : "Carga al menos un expediente antes de procesar la ronda."}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<PlayCircleOutlineIcon />}
          onClick={() => setConfirmOpen(true)}
          disabled={!canProcess}
        >
          {processing ? "Procesando…" : "Procesar ronda"}
        </Button>
      </Stack>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Procesar la ronda {roundId}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Esto arranca el procesamiento por lotes sobre los expedientes de la
            ronda. La operación corre en segundo plano y no puede deshacerse
            desde aquí. ¿Deseas continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleProcess} autoFocus>
            Sí, procesar
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
